import { useCallback, useEffect, useRef, useState } from "react";
import {
  HISTORY_PLAYBACK_DELAYS_MS,
  advancePlaybackIndex,
  canStartHistoryPlayback,
  playbackStartIndex,
  type HistoryPlaybackDelayMs,
  type HistoryPlaybackPhase,
} from "./historyPlayback";

export type UseTacticalHistoryPlaybackArgs = {
  eventCount: number;
  historyIndex: number | null;
  setHistoryIndex: (index: number | null) => void;
  /** Перед входом в as-of кадр (сброс draft/select). */
  onEnterHistoryFrame?: () => void;
  /** Смена доски / ключ сброса → phase idle (без трогания index — это делает caller). */
  resetKey?: number | string | null;
};

export type UseTacticalHistoryPlaybackResult = {
  phase: HistoryPlaybackPhase;
  delayMs: HistoryPlaybackDelayMs;
  setDelayMs: (ms: HistoryPlaybackDelayMs) => void;
  canPlay: boolean;
  play: () => void;
  pause: () => void;
  stop: () => void;
  /** Ручной scrub: Pause + индекс (или live). */
  scrub: (index: number | null) => void;
  delays: typeof HISTORY_PLAYBACK_DELAYS_MS;
};

export function useTacticalHistoryPlayback(
  args: UseTacticalHistoryPlaybackArgs,
): UseTacticalHistoryPlaybackResult {
  const { eventCount, historyIndex, setHistoryIndex, onEnterHistoryFrame, resetKey } =
    args;
  const [phase, setPhase] = useState<HistoryPlaybackPhase>("idle");
  const [delayMs, setDelayMs] = useState<HistoryPlaybackDelayMs>(
    HISTORY_PLAYBACK_DELAYS_MS[1],
  );

  const phaseRef = useRef(phase);
  const historyIndexRef = useRef(historyIndex);
  const eventCountRef = useRef(eventCount);
  const delayRef = useRef(delayMs);
  const setHistoryIndexRef = useRef(setHistoryIndex);
  const onEnterRef = useRef(onEnterHistoryFrame);

  phaseRef.current = phase;
  historyIndexRef.current = historyIndex;
  eventCountRef.current = eventCount;
  delayRef.current = delayMs;
  setHistoryIndexRef.current = setHistoryIndex;
  onEnterRef.current = onEnterHistoryFrame;

  const canPlay = canStartHistoryPlayback(eventCount);

  const pause = useCallback(() => {
    setPhase((p) => (p === "playing" || p === "paused" ? "paused" : "idle"));
  }, []);

  const stop = useCallback(() => {
    setPhase("idle");
    setHistoryIndexRef.current(null);
  }, []);

  const scrub = useCallback((index: number | null) => {
    if (phaseRef.current === "playing") {
      setPhase("paused");
    } else if (index != null && phaseRef.current === "idle") {
      setPhase("paused");
    } else if (index == null) {
      setPhase("idle");
    }
    if (index != null) {
      onEnterRef.current?.();
    }
    setHistoryIndexRef.current(index);
  }, []);

  const play = useCallback(() => {
    const n = eventCountRef.current;
    if (!canStartHistoryPlayback(n)) return;
    const start = playbackStartIndex(historyIndexRef.current, n);
    if (start == null) return;
    onEnterRef.current?.();
    setHistoryIndexRef.current(start);
    setPhase("playing");
  }, []);

  // Тик Play
  useEffect(() => {
    if (phase !== "playing") return;
    const id = window.setInterval(() => {
      const n = eventCountRef.current;
      const cur = historyIndexRef.current;
      if (cur == null || n <= 0) {
        setPhase("idle");
        return;
      }
      const { index, ended } = advancePlaybackIndex(cur, n);
      if (index !== cur) {
        onEnterRef.current?.();
        setHistoryIndexRef.current(index);
      }
      if (ended) {
        setPhase("paused");
      }
    }, delayRef.current);
    return () => window.clearInterval(id);
  }, [phase, delayMs]);

  // Смена доски
  useEffect(() => {
    setPhase("idle");
  }, [resetKey]);

  // Опустевшая лента
  useEffect(() => {
    if (eventCount <= 0 && phase !== "idle") {
      setPhase("idle");
      setHistoryIndex(null);
    }
  }, [eventCount, phase, setHistoryIndex]);

  return {
    phase,
    delayMs,
    setDelayMs,
    canPlay,
    play,
    pause,
    stop,
    scrub,
    delays: HISTORY_PLAYBACK_DELAYS_MS,
  };
}
